import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, CheckCircle2, XCircle, ChevronDown, Unplug } from "lucide-react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SignatureEditor } from "@/components/mail/SignatureEditor";

const PRESETS = {
  webador: { label: "Webador", imap_host: "mail.webador.com", imap_port: 993, imap_security: "ssl", smtp_host: "mail.webador.com", smtp_port: 587, smtp_security: "starttls", hint: "Server Webador già impostati: scrivi solo l'indirizzo email e la password della casella (in Webador: Email → la tua casella → password; può essere diversa da quella dell'account Webador)." },
  gmail: { label: "Gmail", imap_host: "imap.gmail.com", imap_port: 993, imap_security: "ssl", smtp_host: "smtp.gmail.com", smtp_port: 465, smtp_security: "ssl", hint: "Con Gmail serve una «password per le app»: Account Google → Sicurezza → Verifica in due passaggi → Password per le app." },
  aruba: { label: "Aruba", imap_host: "imaps.aruba.it", imap_port: 993, imap_security: "ssl", smtp_host: "smtps.aruba.it", smtp_port: 465, smtp_security: "ssl" },
  altro: { label: "Altro server" },
};
const server = (k) => { const { label, hint, ...s } = PRESETS[k]; return s; };
const detect = (host) => Object.keys(PRESETS).find((k) => PRESETS[k].imap_host === host) || "altro";
const blank = () => ({ email: "", password: "", from_name: "", username: "", ...server("webador") });

const Sec = ({ value, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="ssl">SSL/TLS</SelectItem><SelectItem value="starttls">STARTTLS</SelectItem></SelectContent>
  </Select>
);

const TestRow = ({ label, r }) => (
  <div className={`flex items-center gap-2 text-sm ${r.ok ? "text-emerald-600" : "text-rose-600"}`} data-testid={`mail-test-${label.toLowerCase()}`}>
    {r.ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />} {label}: {r.ok ? "OK" : r.error}
  </div>
);

export const MailSettings = ({ onSaved }) => {
  const [acc, setAcc] = useState(null);
  const [f, setF] = useState(blank());
  const [preset, setPreset] = useState("webador");
  const [adv, setAdv] = useState(false);
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  useEffect(() => {
    api.get("/mail/account").then((r) => {
      setAcc(r.data);
      if (r.data.configured) { setF((x) => ({ ...x, ...r.data, password: "" })); setPreset(detect(r.data.imap_host)); }
    }).catch(() => setAcc({ configured: false }));
  }, []);

  const choose = (k) => { setPreset(k); if (k !== "altro") setF((x) => ({ ...x, ...server(k) })); setAdv(k === "altro"); };
  const run = async (fn) => {
    setBusy(true); setTest(null);
    try { await fn(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const save = () => run(async () => {
    const r = await api.put("/mail/account", { ...f, imap_port: Number(f.imap_port), smtp_port: Number(f.smtp_port) });
    setAcc(r.data.account); setTest(r.data.test); setF((x) => ({ ...x, password: "" }));
    if (r.data.test.imap.ok && r.data.test.smtp.ok) toast.success("Casella collegata e verificata");
    else toast.error("Dati salvati, ma la verifica non è riuscita: controlla i dati");
    onSaved?.(r.data.account);
  });
  const verify = () => run(async () => setTest((await api.post("/mail/test")).data));
  const remove = () => run(async () => {
    await api.delete("/mail/account");
    setAcc({ configured: false }); setF(blank()); setPreset("webador");
    toast.success("Casella scollegata"); onSaved?.({ configured: false });
  });

  if (!acc) return null;
  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-5" data-testid="mail-settings">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Mail className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-semibold">Casella email (IMAP/SMTP)</h3>
          <p className="text-xs text-muted-foreground">Leggi e invia la posta dall'app e dall'assistente IA. La password è salvata cifrata.</p>
        </div>
        {acc.configured && <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" data-testid="mail-connected-badge">Collegata</span>}
      </div>
      <div className="flex gap-2 flex-wrap">
        {Object.entries(PRESETS).map(([k, p]) => (
          <button key={k} type="button" onClick={() => choose(k)} className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${preset === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`} data-testid={`mail-preset-${k}`}>{p.label}</button>
        ))}
      </div>
      {PRESETS[preset].hint && <p className="text-xs text-muted-foreground bg-secondary/60 rounded-lg p-3">{PRESETS[preset].hint}</p>}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Indirizzo email</Label><Input type="email" value={f.email} onChange={set("email")} placeholder="info@tuaditta.it" data-testid="mail-email-input" /></div>
        <div className="space-y-2"><Label>Password della casella</Label><Input type="password" value={f.password} onChange={set("password")} placeholder={acc.has_password ? "•••••••• (salvata, lascia vuoto per non cambiarla)" : "Password"} autoComplete="new-password" data-testid="mail-password-input" /></div>
        <div className="space-y-2 sm:col-span-2"><Label>Nome mittente</Label><Input value={f.from_name || ""} onChange={set("from_name")} placeholder="Es. Edil Rossi Srl" data-testid="mail-from-name-input" /></div>
      </div>
      <button type="button" onClick={() => setAdv((v) => !v)} className="text-sm text-primary font-medium flex items-center gap-1" data-testid="mail-advanced-toggle">
        <ChevronDown className={`w-4 h-4 transition-transform ${adv ? "rotate-180" : ""}`} /> Impostazioni server
      </button>
      {adv && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 rounded-lg border border-border p-4">
          <div className="space-y-1.5 col-span-2"><Label className="text-xs">Server in arrivo (IMAP)</Label><Input value={f.imap_host} onChange={set("imap_host")} data-testid="mail-imap-host-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Porta</Label><Input type="number" value={f.imap_port} onChange={set("imap_port")} data-testid="mail-imap-port-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Sicurezza</Label><Sec value={f.imap_security} onChange={(v) => setF((x) => ({ ...x, imap_security: v }))} testid="mail-imap-security" /></div>
          <div className="space-y-1.5 col-span-2"><Label className="text-xs">Server in uscita (SMTP)</Label><Input value={f.smtp_host} onChange={set("smtp_host")} data-testid="mail-smtp-host-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Porta</Label><Input type="number" value={f.smtp_port} onChange={set("smtp_port")} data-testid="mail-smtp-port-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Sicurezza</Label><Sec value={f.smtp_security} onChange={(v) => setF((x) => ({ ...x, smtp_security: v }))} testid="mail-smtp-security" /></div>
          <div className="space-y-1.5 col-span-2 sm:col-span-4"><Label className="text-xs">Nome utente (se diverso dall'email)</Label><Input value={f.username || ""} onChange={set("username")} data-testid="mail-username-input" /></div>
        </div>
      )}
      {test && <div className="space-y-1 rounded-lg bg-secondary/50 p-3"><TestRow label="IMAP" r={test.imap} /><TestRow label="SMTP" r={test.smtp} /></div>}
      <div className="flex gap-2 flex-wrap justify-end">
        {acc.configured && <Button variant="ghost" onClick={remove} disabled={busy} className="text-destructive mr-auto" data-testid="mail-disconnect-button"><Unplug className="w-4 h-4 mr-1.5" /> Scollega</Button>}
        {acc.configured && <Button variant="outline" onClick={verify} disabled={busy} data-testid="mail-test-button">Verifica connessione</Button>}
        <Button onClick={save} disabled={busy || !f.email || (!f.password && !acc.has_password)} data-testid="mail-save-button">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null} Salva e verifica
        </Button>
      </div>
      {acc.configured && <SignatureEditor />}
    </div>
  );
};

export default MailSettings;
